// Translation strings for the onboarding-form microapp.
// Mirrors the workflow-hub-v2 approach: a flat key map per locale, with {var} placeholders
// resolved by useTranslation's `vars` argument.
//
// To add a string:
//   1. Add the key to TranslationKey
//   2. Add it to BOTH the `en` and `fr` blocks below (TS will fail the build otherwise)
//   3. Replace the inline literal in the component with `t('yourKey')` (or `t('yourKey', { name })` for interpolation)
//
// French copy is a starting draft — please have a fluent reviewer pass over it before shipping.

export type TranslationKey =
  // Common actions / verbs
  | "cancel"
  | "close"
  | "proceed"
  | "starting"
  | "yesEditEmail"
  | "yesSwitch"
  | "yesCreateNew"
  | "yesContinue"
  | "applyAndReview"
  | "useExisting"
  | "useExistingProfile"
  | "edit"

  // Drawer shell
  | "fromSource"
  | "closeDetailsPanel"

  // Loading / error shell
  | "noOnboardingId"

  // Section headers
  | "sectionMapping"
  | "sectionWorkerSetup"
  | "sectionDatesRates"
  | "sectionTemplate"

  // Field labels
  | "labelInternalCompany"
  | "labelClientOrganization"
  | "labelLineOfBusiness"
  | "labelEmailAddress"
  | "labelProfileType"
  | "labelWorker"
  | "labelWorkerOrganization"
  | "labelStartDate"
  | "labelEndDate"
  | "labelBillRate"
  | "labelPayRate"
  | "labelBillCurrency"
  | "labelPayCurrency"
  | "labelChooseTemplate"
  | "labelConfirmNewWorkerOrg"

  // Field placeholders
  | "placeholderSelectInternalCompany"
  | "placeholderSelectClientOrg"
  | "placeholderSelectLob"
  | "placeholderSelectProfileType"
  | "placeholderSelectWorker"
  | "placeholderSelectWorkerOrg"
  | "placeholderEnterName"
  | "placeholderEmail"
  | "placeholderDate"
  | "placeholderSelect"
  | "placeholderSearch"
  | "placeholderTypeToSearch"
  | "placeholderSearchOrCreate"
  | "placeholderSelectTemplate"
  | "placeholderSelectClientOrgForTemplates"
  | "placeholderSelectLobForTemplates"
  | "placeholderNoTemplatesForLob"
  | "noTemplatesForClientLob"

  // Worker locked-state messages
  | "enterEmailFirst"
  | "selectProfileTypeFirst"
  | "selectWorkerFirst"

  // Inline confirmations / banners
  | "editingEmailWillDiscard"
  | "switchingProfileTypeWillDiscard"
  | "discard"

  // Worker creation flow
  | "addNewWorkerContact"
  | "existingContactWithProfileTitle"
  | "existingContactWithProfileBody"
  | "existingContactsMatchEmail"
  | "existingContactsBadge"
  | "createNewContactConfirm"
  | "secondProfileSameType"
  | "differentOrganization"
  | "newContact"
  | "newProfile"
  | "existingProfile"
  | "newProfileAddedToExistingContact"
  | "newContactAndProfileWillBeCreated"
  | "existingProfileAndContactWillBeUsed"

  // Name mismatch warning
  | "nameMismatchWarning"
  | "source"

  // Worker organization
  | "searchOrganization"
  | "pickOrCreateRealOrg"
  | "iDontKnow"
  | "createPlaceholder"
  | "placeholderOrgWillBeCreated"
  | "placeholderName"
  | "newOrgWillBeCreated"
  | "linkedToExistingOrg"
  | "newOrganization"
  | "placeholderOrganization"
  | "newWorkerOrgWillBeCreated"
  | "workerOrgInputMode"
  | "createNewWorkerOrganization"
  | "unlockToChangeOrg"
  | "unlockWorkerOrg"
  | "cancelNewWorkerOrg"
  | "editNewWorkerOrgName"
  | "confirmNewWorkerOrgName"
  | "nameCannotBeEmpty"
  | "orgAlreadyExistsPickInstead"
  | "workerOrgOptionalNote"
  | "orgAlreadyHasProfileWarning"

  // Pills / badges
  | "changed"
  | "locked"
  | "willCreate"
  | "undoCreation"

  // Status banners
  | "blockedPendingProfile"
  | "openPendingProfileInFbo"
  | "clickHere"
  | "clientOrgMustBeCreated"

  // Status-blocked tooltip
  | "blockedPendingChanges"
  | "clientOrgMustBeCreatedFirst"

  // Submission feedback
  | "draftWoCreatedSuccess"
  | "alreadyOnboarded"
  | "partialSubmission"
  | "submissionFailedTryAgain"
  | "missingRequiredFields"
  | "completeAllRequiredFields"
  | "confirmEmailBeforeSubmit"

  // What happens next section
  | "whatHappensNext"
  | "whatHappensNextBody"
  | "draftWorkOrderCreated"
  | "recordRemovedFromSource"
  | "appearsInOnboardingTab"
  | "thisActionWillCreate"
  | "whatWillHappen"

  // Creation items
  | "creationItemWorkerContact"
  | "creationItemWorkerProfile"
  | "creationItemWorkerOrg"
  | "creationItemPlaceholderOrg"

  // Source value display
  | "notAvailable"

  // Searchable select
  | "selectEllipsis"
  | "searching"
  | "noResultsFound"
  | "createNew"
  | "createNewLabeled"
  | "fromSourceLabel"
  | "possibleMatchesAbove"
  | "continueCreatingNewEntry"
  | "clearSelection"
  | "undoCreate"
  | "confirmEmail"
  | "cancelEmailEdit"
  | "editEmail"

  // Validation errors
  | "errorInternalCompanyRequired"
  | "errorClientOrgRequired"
  | "errorEmailRequired"
  | "errorProfileTypeRequired"
  | "errorWorkerRequired"
  | "errorWorkerOrgRequired"
  | "errorLobRequired"
  | "errorStartDateRequired"
  | "errorStartDateInvalid"
  | "errorEndDateRequired"
  | "errorEndDateInvalid"
  | "errorEndBeforeStart"
  | "errorBillRateRequired"
  | "errorPayRateRequired"
  | "errorBillCurrencyRequired"
  | "errorPayCurrencyRequired"
  | "errorPayHigherThanBill"

  // Default/fallback labels
  | "unknownWorker"

  // Worker create warning
  | "creatingContactExistingFound"

  // Misc
  | "payRateCurrencyWarning"
  | "creationItemWorkerProfileWithType"
  | "creationItemWorkerOrgWithName"
  | "creationItemPlaceholderOrgWithName"
  | "templatePlaceholderSelectClient"
  | "templatePlaceholderSelectLob"
  | "templatePlaceholderNoTemplates"
  | "templatePlaceholderSelect"
  | "recordNounDetails"

  // Preferred Language
  | "labelPreferredLanguage"
  | "languageEnglish"
  | "languageFrench"
  | "preferredLanguageManagedInFbo"
  | "openContactInFbo"
  | "preferredLanguageConfirmWarning"
  | "preferredLanguageChangeConfirmTitle"
  | "preferredLanguageChangeConfirmBody"
  | "yesChangeLanguage"
  | "preferredLanguageReadOnly"
  | "preferredLanguageReadOnlyMismatch"

  // Contact status labels
  | "statusActive"
  | "statusInactive"
  | "statusPendingApproval"
  | "statusPendingChange"
  | "statusTerminated"

export const translations: Record<"en" | "fr", Record<TranslationKey, string>> = {
  en: {
    // Common actions / verbs
    cancel: "Cancel",
    close: "Close",
    proceed: "Proceed",
    starting: "Starting...",
    yesEditEmail: "Yes, edit email",
    yesSwitch: "Yes, switch",
    yesCreateNew: "Yes, create new",
    yesContinue: "Yes, continue",
    applyAndReview: "Apply and review",
    useExisting: "Use existing",
    useExistingProfile: "Use existing profile",
    edit: "Edit",

    // Drawer shell
    fromSource: "From {sourceName}",
    closeDetailsPanel: "Close details panel",

    // Loading / error shell
    noOnboardingId: "No onboarding ID provided.",

    // Section headers
    sectionMapping: "Mapping",
    sectionWorkerSetup: "Worker Setup",
    sectionDatesRates: "Dates & Rates",
    sectionTemplate: "Template",

    // Field labels
    labelInternalCompany: "Internal Company",
    labelClientOrganization: "Client Organization",
    labelLineOfBusiness: "Line of Business (LOB)",
    labelEmailAddress: "Email address",
    labelProfileType: "Profile Type",
    labelWorker: "Worker",
    labelWorkerOrganization: "Worker Organization",
    labelStartDate: "Start Date",
    labelEndDate: "End Date",
    labelBillRate: "Bill Rate",
    labelPayRate: "Pay Rate",
    labelBillCurrency: "Bill Currency",
    labelPayCurrency: "Pay Currency",
    labelChooseTemplate: "Choose Template",
    labelConfirmNewWorkerOrg: "Confirm new worker organization",

    // Field placeholders
    placeholderSelectInternalCompany: "Select internal company...",
    placeholderSelectClientOrg: "Select client organization...",
    placeholderSelectLob: "Select LOB...",
    placeholderSelectProfileType: "Select profile type...",
    placeholderSelectWorker: "Select Worker",
    placeholderSelectWorkerOrg: "Select worker organization...",
    placeholderEnterName: "Enter name...",
    placeholderEmail: "worker@example.com",
    placeholderDate: "YYYY-MM-DD",
    placeholderSelect: "Select...",
    placeholderSearch: "Search...",
    placeholderTypeToSearch: "Type to search...",
    placeholderSearchOrCreate: "Search or type to create...",
    placeholderSelectTemplate: "Select template...",
    placeholderSelectClientOrgForTemplates: "Select Client Organization to load templates",
    placeholderSelectLobForTemplates: "Select LOB to load templates",
    placeholderNoTemplatesForLob: "No templates available for this LOB and Client.",
    noTemplatesForClientLob: "No templates available for this client and LOB.",

    // Worker locked-state messages
    enterEmailFirst: "Enter and confirm email address first",
    selectProfileTypeFirst: "Select profile type first",
    selectWorkerFirst: "Select Worker first",

    // Inline confirmations / banners
    editingEmailWillDiscard: "Editing the email will {discard} the selected worker and worker organization. Continue?",
    switchingProfileTypeWillDiscard: 'Switching to "{profileType}" will {discard} the selected worker and worker organization. Continue?',
    discard: "discard",

    // Worker creation flow
    addNewWorkerContact: "Add new worker contact",
    existingContactWithProfileTitle: "An existing contact already has a {profileType} profile",
    existingContactWithProfileBody: "Creating a new contact will produce a second profile of the same type. Continue only if this is intentional (e.g. you need to associate a different organization).",
    existingContactsMatchEmail: "{badge} already match this email",
    existingContactsBadge: "{count} existing contact{plural}",
    createNewContactConfirm: "Are you sure you want to create a new contact instead of selecting one above?",
    secondProfileSameType: "second profile of the same type",
    differentOrganization: "different organization",
    newContact: "New contact",
    newProfile: "New profile",
    existingProfile: "Existing profile",
    newProfileAddedToExistingContact: "added to an existing contact",
    newContactAndProfileWillBeCreated: "and {newProfile} will be created",
    existingProfileAndContactWillBeUsed: "and contact will be used",

    // Name mismatch warning
    nameMismatchWarning: "Name doesn't match {source} value [{source}: {name}]",
    source: "source",

    // Worker organization
    searchOrganization: "Search organization",
    pickOrCreateRealOrg: "Pick or create a real org",
    iDontKnow: "I don't know",
    createPlaceholder: "Create a placeholder",
    placeholderOrgWillBeCreated: "A placeholder organization will be created on submit so the placement can proceed. It will be automatically renamed as part of the onboarding process.",
    placeholderName: "Placeholder name: {name}",
    newOrgWillBeCreated: 'New worker organization "{name}" will be created.',
    linkedToExistingOrg: "Linked to existing organization",
    newOrganization: "New organization",
    placeholderOrganization: "Placeholder organization",
    newWorkerOrgWillBeCreated: "will be created",
    workerOrgInputMode: "Worker organization input mode",
    createNewWorkerOrganization: "Create new Worker Organization",
    unlockToChangeOrg: "Unlock to change organization",
    unlockWorkerOrg: "Unlock worker organization",
    cancelNewWorkerOrg: "Cancel new worker organization",
    editNewWorkerOrgName: "Edit new worker organization name",
    confirmNewWorkerOrgName: "Confirm new worker organization name",
    nameCannotBeEmpty: "Name cannot be empty.",
    orgAlreadyExistsPickInstead: 'A worker organization named "{name}" already exists. Pick it instead of creating a duplicate.',
    workerOrgOptionalNote: "Optional — assign or create an organization for this new worker.",
    orgAlreadyHasProfileWarning: "{org} already has associated {profileType} profile. You can proceed with this selection, or use the existing profile.",

    // Pills / badges
    changed: "Changed",
    locked: "Locked",
    willCreate: "Will create:",
    undoCreation: "Undo creation",

    // Status banners
    blockedPendingProfile: "You cannot edit this contact or create new profiles until all pending changes have been approved. To resolve this {action}",
    openPendingProfileInFbo: "open the pending profile in FBO",
    clickHere: "click here",
    clientOrgMustBeCreated: "Client organization must be created before proceeding.",

    // Status-blocked tooltip
    blockedPendingChanges: "Blocked: pending contact changes",
    clientOrgMustBeCreatedFirst: "Client organization must be created first",

    // Submission feedback
    draftWoCreatedSuccess: "Draft work order successfully created",
    alreadyOnboarded: "This placement has already been onboarded. A work order was created from a previous submission.",
    partialSubmission: "Part of the submission completed before an error occurred. Review the changes below and resubmit.",
    submissionFailedTryAgain: "Submission failed. Please try again.",
    missingRequiredFields: "Missing required fields:",
    completeAllRequiredFields: "Complete all required fields",
    confirmEmailBeforeSubmit: "Confirm or cancel email change before submitting",

    // What happens next section
    whatHappensNext: "What happens next",
    whatHappensNextBody: "Starting onboarding creates a draft work order. This {recordNoun} will be hidden from the {sourceName} and appear in the Onboarding tab.",
    draftWorkOrderCreated: "Draft Work Order created",
    recordRemovedFromSource: "{recordNoun} removed from {sourceName}",
    appearsInOnboardingTab: "Appears in Onboarding tab",
    thisActionWillCreate: "This action will also create:",
    whatWillHappen: "What will happen",

    // Creation items
    creationItemWorkerContact: "Worker Contact",
    creationItemWorkerProfile: "Worker Profile",
    creationItemWorkerOrg: "Worker Organization",
    creationItemPlaceholderOrg: "Placeholder Worker Organization",

    // Source value display
    notAvailable: "N/A",

    // Searchable select
    selectEllipsis: "Select...",
    searching: "Searching...",
    noResultsFound: "No results found.",
    createNew: "Create new",
    createNewLabeled: '{label}: "{name}"',
    fromSourceLabel: "(from {source})",
    possibleMatchesAbove: "{count} possible match{plural} above",
    continueCreatingNewEntry: "Continue creating a new entry instead of picking one of the existing options?",
    clearSelection: "Clear selection",
    undoCreate: "Undo creation",
    confirmEmail: "Confirm email",
    cancelEmailEdit: "Cancel email edit",
    editEmail: "Edit email",

    // Validation errors
    errorInternalCompanyRequired: "Internal Company is required.",
    errorClientOrgRequired: "Client Organization is required.",
    errorEmailRequired: "Email address is required.",
    errorProfileTypeRequired: "Profile Type is required.",
    errorWorkerRequired: "Worker is required.",
    errorWorkerOrgRequired: "Worker Organization is required for this profile type.",
    errorLobRequired: "Line of Business is required.",
    errorStartDateRequired: "Start Date is required.",
    errorStartDateInvalid: "Start Date must be a valid date.",
    errorEndDateRequired: "End Date is required.",
    errorEndDateInvalid: "End Date must be a valid date.",
    errorEndBeforeStart: "End date cannot be before start date.",
    errorBillRateRequired: "Bill Rate is required.",
    errorPayRateRequired: "Pay Rate is required.",
    errorBillCurrencyRequired: "Bill Currency is required.",
    errorPayCurrencyRequired: "Pay Currency is required.",
    errorPayHigherThanBill: "Pay Rate cannot be higher than Bill Rate",

    // Default/fallback labels
    unknownWorker: "Unknown worker",

    // Worker create warning
    creatingContactExistingFound: "{count} existing contact(s) found for this email — undo to select one instead",

    // Misc
    payRateCurrencyWarning: "Pay Rate and Bill Rate are in different currencies — please verify these rates are intentional",
    creationItemWorkerProfileWithType: "Worker Profile ({profileType})",
    creationItemWorkerOrgWithName: "Worker Organization: {name}",
    creationItemPlaceholderOrgWithName: "Worker Organization (placeholder): {name}",
    templatePlaceholderSelectClient: "Select Client Organization to load templates",
    templatePlaceholderSelectLob: "Select LOB to load templates",
    templatePlaceholderNoTemplates: "No templates available for this LOB and Client.",
    templatePlaceholderSelect: "Select template...",
    recordNounDetails: "{recordNoun} Details",

    // Preferred Language
    labelPreferredLanguage: "Preferred Language",
    languageEnglish: "English",
    languageFrench: "French",
    preferredLanguageManagedInFbo: "The contact can update their preferred language later from Contractor Portal.",
    openContactInFbo: "Open in FBO",
    preferredLanguageConfirmWarning: "This will set the new contact's application language in FBO. Confirm it matches the worker's preference before submitting — they can update it later from Contractor Portal.",
    preferredLanguageChangeConfirmTitle: "Change preferred language?",
    preferredLanguageChangeConfirmBody: "This will replace the suggested language ({current}) with {pending} and become their FBO application language.",
    yesChangeLanguage: "Yes, change language",
    preferredLanguageReadOnly: "This contact already has their preferred language set to {language}. It can't be changed from this form.",
    preferredLanguageReadOnlyMismatch: "This contact has their preferred language set to {fboLanguage} in FBO, but the request indicates {requestLanguage}. The FBO value applies and can't be changed from this form.",

    // Contact status labels
    statusActive: "Active",
    statusInactive: "Inactive",
    statusPendingApproval: "Pending Approval",
    statusPendingChange: "Pending Change",
    statusTerminated: "Terminated",
  },

  fr: {
    // Common actions / verbs
    cancel: "Annuler",
    close: "Fermer",
    proceed: "Continuer",
    starting: "Démarrage...",
    yesEditEmail: "Oui, modifier le courriel",
    yesSwitch: "Oui, changer",
    yesCreateNew: "Oui, créer",
    yesContinue: "Oui, continuer",
    applyAndReview: "Appliquer et vérifier",
    useExisting: "Utiliser l'existant",
    useExistingProfile: "Utiliser le profil existant",
    edit: "Modifier",

    // Drawer shell
    fromSource: "Depuis {sourceName}",
    closeDetailsPanel: "Fermer le panneau de détails",

    // Loading / error shell
    noOnboardingId: "Aucun identifiant d'intégration fourni.",

    // Section headers
    sectionMapping: "Correspondance",
    sectionWorkerSetup: "Configuration du travailleur",
    sectionDatesRates: "Dates et taux",
    sectionTemplate: "Modèle",

    // Field labels
    labelInternalCompany: "Société interne",
    labelClientOrganization: "Organisation cliente",
    labelLineOfBusiness: "Secteur d'activité (LOB)",
    labelEmailAddress: "Adresse courriel",
    labelProfileType: "Type de profil",
    labelWorker: "Travailleur",
    labelWorkerOrganization: "Organisation du travailleur",
    labelStartDate: "Date de début",
    labelEndDate: "Date de fin",
    labelBillRate: "Taux facturé",
    labelPayRate: "Taux payé",
    labelBillCurrency: "Devise de facturation",
    labelPayCurrency: "Devise de paie",
    labelChooseTemplate: "Choisir un modèle",
    labelConfirmNewWorkerOrg: "Confirmer la nouvelle organisation du travailleur",

    // Field placeholders
    placeholderSelectInternalCompany: "Sélectionner une société interne...",
    placeholderSelectClientOrg: "Sélectionner une organisation cliente...",
    placeholderSelectLob: "Sélectionner un LOB...",
    placeholderSelectProfileType: "Sélectionner un type de profil...",
    placeholderSelectWorker: "Sélectionner un travailleur",
    placeholderSelectWorkerOrg: "Sélectionner une organisation...",
    placeholderEnterName: "Entrer un nom...",
    placeholderEmail: "travailleur@exemple.com",
    placeholderDate: "AAAA-MM-JJ",
    placeholderSelect: "Sélectionner...",
    placeholderSearch: "Rechercher...",
    placeholderTypeToSearch: "Taper pour rechercher...",
    placeholderSearchOrCreate: "Rechercher ou taper pour créer...",
    placeholderSelectTemplate: "Sélectionner un modèle...",
    placeholderSelectClientOrgForTemplates: "Sélectionner une organisation cliente pour charger les modèles",
    placeholderSelectLobForTemplates: "Sélectionner un LOB pour charger les modèles",
    placeholderNoTemplatesForLob: "Aucun modèle disponible pour ce LOB et ce client.",
    noTemplatesForClientLob: "Aucun modèle disponible pour ce client et ce LOB.",

    // Worker locked-state messages
    enterEmailFirst: "Entrez et confirmez d'abord l'adresse courriel",
    selectProfileTypeFirst: "Sélectionnez d'abord le type de profil",
    selectWorkerFirst: "Sélectionnez d'abord un travailleur",

    // Inline confirmations / banners
    editingEmailWillDiscard: "Modifier le courriel va {discard} le travailleur et l'organisation sélectionnés. Continuer?",
    switchingProfileTypeWillDiscard: "Passer à « {profileType} » va {discard} le travailleur et l'organisation sélectionnés. Continuer?",
    discard: "annuler",

    // Worker creation flow
    addNewWorkerContact: "Ajouter un nouveau contact travailleur",
    existingContactWithProfileTitle: "Un contact existant possède déjà un profil {profileType}",
    existingContactWithProfileBody: "Créer un nouveau contact va produire un second profil du même type. Continuez seulement si c'est intentionnel (par exemple, pour associer une organisation différente).",
    existingContactsMatchEmail: "{badge} correspond(ent) déjà à ce courriel",
    existingContactsBadge: "{count} contact{plural} existant{plural}",
    createNewContactConfirm: "Êtes-vous sûr de vouloir créer un nouveau contact plutôt que d'en sélectionner un ci-dessus?",
    secondProfileSameType: "second profil du même type",
    differentOrganization: "organisation différente",
    newContact: "Nouveau contact",
    newProfile: "Nouveau profil",
    existingProfile: "Profil existant",
    newProfileAddedToExistingContact: "ajouté à un contact existant",
    newContactAndProfileWillBeCreated: "et un {newProfile} seront créés",
    existingProfileAndContactWillBeUsed: "et le contact seront utilisés",

    // Name mismatch warning
    nameMismatchWarning: "Le nom ne correspond pas à la valeur de {source} [{source} : {name}]",
    source: "la source",

    // Worker organization
    searchOrganization: "Rechercher une organisation",
    pickOrCreateRealOrg: "Choisir ou créer une vraie organisation",
    iDontKnow: "Je ne sais pas",
    createPlaceholder: "Créer un substitut",
    placeholderOrgWillBeCreated: "Une organisation substitut sera créée à la soumission afin que le placement puisse continuer. Elle sera renommée automatiquement durant le processus d'intégration.",
    placeholderName: "Nom du substitut : {name}",
    newOrgWillBeCreated: "La nouvelle organisation du travailleur « {name} » sera créée.",
    linkedToExistingOrg: "Lié à une organisation existante",
    newOrganization: "Nouvelle organisation",
    placeholderOrganization: "Organisation substitut",
    newWorkerOrgWillBeCreated: "sera créée",
    workerOrgInputMode: "Mode de saisie de l'organisation du travailleur",
    createNewWorkerOrganization: "Créer une nouvelle organisation du travailleur",
    unlockToChangeOrg: "Déverrouiller pour changer l'organisation",
    unlockWorkerOrg: "Déverrouiller l'organisation du travailleur",
    cancelNewWorkerOrg: "Annuler la nouvelle organisation du travailleur",
    editNewWorkerOrgName: "Modifier le nom de la nouvelle organisation du travailleur",
    confirmNewWorkerOrgName: "Confirmer le nom de la nouvelle organisation du travailleur",
    nameCannotBeEmpty: "Le nom ne peut pas être vide.",
    orgAlreadyExistsPickInstead: "Une organisation du travailleur nommée « {name} » existe déjà. Sélectionnez-la plutôt que de créer un doublon.",
    workerOrgOptionalNote: "Facultatif — assigner ou créer une organisation pour ce nouveau travailleur.",
    orgAlreadyHasProfileWarning: "{org} possède déjà un profil {profileType} associé. Vous pouvez continuer avec cette sélection ou utiliser le profil existant.",

    // Pills / badges
    changed: "Modifié",
    locked: "Verrouillé",
    willCreate: "Créera :",
    undoCreation: "Annuler la création",

    // Status banners
    blockedPendingProfile: "Vous ne pouvez pas modifier ce contact ni créer de nouveaux profils tant que tous les changements en attente n'ont pas été approuvés. Pour résoudre cela, {action}",
    openPendingProfileInFbo: "ouvrez le profil en attente dans FBO",
    clickHere: "cliquez ici",
    clientOrgMustBeCreated: "L'organisation cliente doit être créée avant de continuer.",

    // Status-blocked tooltip
    blockedPendingChanges: "Bloqué : changements de contact en attente",
    clientOrgMustBeCreatedFirst: "L'organisation cliente doit être créée d'abord",

    // Submission feedback
    draftWoCreatedSuccess: "Bon de travail brouillon créé avec succès",
    alreadyOnboarded: "Ce placement a déjà été intégré. Un bon de travail a été créé lors d'une soumission précédente.",
    partialSubmission: "Une partie de la soumission s'est terminée avant qu'une erreur ne se produise. Vérifiez les modifications ci-dessous et soumettez à nouveau.",
    submissionFailedTryAgain: "Échec de la soumission. Veuillez réessayer.",
    missingRequiredFields: "Champs obligatoires manquants :",
    completeAllRequiredFields: "Remplissez tous les champs obligatoires",
    confirmEmailBeforeSubmit: "Confirmez ou annulez la modification du courriel avant de soumettre",

    // What happens next section
    whatHappensNext: "Étapes suivantes",
    whatHappensNextBody: "Démarrer l'intégration crée un bon de travail brouillon. Ce {recordNoun} sera masqué de {sourceName} et apparaîtra dans l'onglet Intégration.",
    draftWorkOrderCreated: "Bon de travail brouillon créé",
    recordRemovedFromSource: "{recordNoun} retiré de {sourceName}",
    appearsInOnboardingTab: "Apparaît dans l'onglet Intégration",
    thisActionWillCreate: "Cette action va également créer :",
    whatWillHappen: "Ce qui va se passer",

    // Creation items
    creationItemWorkerContact: "Contact travailleur",
    creationItemWorkerProfile: "Profil travailleur",
    creationItemWorkerOrg: "Organisation du travailleur",
    creationItemPlaceholderOrg: "Organisation substitut du travailleur",

    // Source value display
    notAvailable: "S/O",

    // Searchable select
    selectEllipsis: "Sélectionner...",
    searching: "Recherche...",
    noResultsFound: "Aucun résultat trouvé.",
    createNew: "Créer",
    createNewLabeled: "{label} : « {name} »",
    fromSourceLabel: "(depuis {source})",
    possibleMatchesAbove: "{count} correspondance{plural} possible{plural} ci-dessus",
    continueCreatingNewEntry: "Continuer la création d'une nouvelle entrée plutôt que de choisir une option existante?",
    clearSelection: "Effacer la sélection",
    undoCreate: "Annuler la création",
    confirmEmail: "Confirmer le courriel",
    cancelEmailEdit: "Annuler la modification du courriel",
    editEmail: "Modifier le courriel",

    // Validation errors
    errorInternalCompanyRequired: "La société interne est requise.",
    errorClientOrgRequired: "L'organisation cliente est requise.",
    errorEmailRequired: "L'adresse courriel est requise.",
    errorProfileTypeRequired: "Le type de profil est requis.",
    errorWorkerRequired: "Le travailleur est requis.",
    errorWorkerOrgRequired: "L'organisation du travailleur est requise pour ce type de profil.",
    errorLobRequired: "Le secteur d'activité est requis.",
    errorStartDateRequired: "La date de début est requise.",
    errorStartDateInvalid: "La date de début doit être une date valide.",
    errorEndDateRequired: "La date de fin est requise.",
    errorEndDateInvalid: "La date de fin doit être une date valide.",
    errorEndBeforeStart: "La date de fin ne peut pas être antérieure à la date de début.",
    errorBillRateRequired: "Le taux facturé est requis.",
    errorPayRateRequired: "Le taux payé est requis.",
    errorBillCurrencyRequired: "La devise de facturation est requise.",
    errorPayCurrencyRequired: "La devise de paie est requise.",
    errorPayHigherThanBill: "Le taux payé ne peut pas être supérieur au taux facturé",

    // Default/fallback labels
    unknownWorker: "Travailleur inconnu",

    // Worker create warning
    creatingContactExistingFound: "{count} contact(s) existant(s) trouvé(s) pour ce courriel — annulez pour en sélectionner un",

    // Misc
    payRateCurrencyWarning: "Le taux payé et le taux facturé sont dans des devises différentes — vérifiez que ces taux sont intentionnels",
    creationItemWorkerProfileWithType: "Profil travailleur ({profileType})",
    creationItemWorkerOrgWithName: "Organisation du travailleur : {name}",
    creationItemPlaceholderOrgWithName: "Organisation du travailleur (substitut) : {name}",
    templatePlaceholderSelectClient: "Sélectionner une organisation cliente pour charger les modèles",
    templatePlaceholderSelectLob: "Sélectionner un LOB pour charger les modèles",
    templatePlaceholderNoTemplates: "Aucun modèle disponible pour ce LOB et ce client.",
    templatePlaceholderSelect: "Sélectionner un modèle...",
    recordNounDetails: "Détails — {recordNoun}",

    // Preferred Language
    labelPreferredLanguage: "Langue préférée",
    languageEnglish: "Anglais",
    languageFrench: "Français",
    preferredLanguageManagedInFbo: "Le contact pourra mettre à jour sa langue préférée plus tard depuis le Portail des entrepreneurs.",
    openContactInFbo: "Ouvrir dans FBO",
    preferredLanguageConfirmWarning: "Ceci définira la langue de l'application FBO du nouveau contact. Confirmez qu'elle correspond à la préférence du travailleur avant de soumettre — il pourra la modifier plus tard depuis le Portail des entrepreneurs.",
    preferredLanguageChangeConfirmTitle: "Modifier la langue préférée?",
    preferredLanguageChangeConfirmBody: "Ceci remplacera la langue suggérée ({current}) par {pending} et deviendra sa langue d'application FBO.",
    yesChangeLanguage: "Oui, changer la langue",
    preferredLanguageReadOnly: "Ce contact a déjà sa langue préférée définie à {language}. Elle ne peut pas être modifiée depuis ce formulaire.",
    preferredLanguageReadOnlyMismatch: "Ce contact a sa langue préférée définie à {fboLanguage} dans FBO, mais la demande indique {requestLanguage}. La valeur FBO s'applique et ne peut pas être modifiée depuis ce formulaire.",

    // Contact status labels
    statusActive: "Actif",
    statusInactive: "Inactif",
    statusPendingApproval: "En attente d'approbation",
    statusPendingChange: "Changement en attente",
    statusTerminated: "Terminé",
  },
}
